"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border shadow-md">
            <div className="aspect-[16/9] w-full animate-pulse bg-muted" />
            <CardHeader>
              <div className="mb-2 h-5 w-24 animate-pulse rounded-md bg-muted" />
              <div className="h-6 w-full animate-pulse rounded-md bg-muted" />
              <div className="h-6 w-2/3 animate-pulse rounded-md bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
                <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
                <div className="h-4 w-4/5 animate-pulse rounded-md bg-muted" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 p-12 text-center">
        <p className="text-muted-foreground font-sans">
          Unable to load the latest legal analysis at this time.
        </p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 p-12 text-center">
        <p className="text-muted-foreground font-sans">
          Check back soon for our latest legal insights and case studies.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const categoryName = post.categories?.[0]?.name || "Legal Brief";
      const postDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      }) : "";
      return <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border bg-card shadow-md transition-all duration-500 hover:shadow-lg">
            <Link to={post.link || "#"} className="relative block aspect-[16/9] w-full overflow-hidden bg-muted">
              <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/legal_office_exterior.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute top-4 left-4">
                <Badge variant="secondary" className="bg-background/95 font-mono text-[10px] uppercase tracking-widest backdrop-blur-sm hover:bg-background">
                  {categoryName}
                </Badge>
              </div>
            </Link>

            <CardHeader className="flex-none pb-4">
              {postDate && <div className="mb-3 flex items-center text-xs font-mono uppercase tracking-wider text-muted-foreground">
                  <SafeHydrate><Calendar className="mr-1.5 size-3.5" /></SafeHydrate>
                  {postDate}
                </div>}
              <CardTitle className="font-serif text-2xl font-bold leading-tight">
                <Link to={post.link || "#"} className="line-clamp-2 text-foreground transition-colors hover:text-primary" dangerouslySetInnerHTML={{
              __html: post.title
            }} />
              </CardTitle>
            </CardHeader>

            <CardContent className="flex-grow pb-6">
              <div className="line-clamp-3 text-base leading-relaxed text-muted-foreground font-sans" dangerouslySetInnerHTML={{
            __html: post.excerpt || "Explore our comprehensive legal analysis on recent legislative changes and their implications for your personal and professional interests."
          }} />
            </CardContent>

            <CardFooter className="mt-auto pt-0">
              <Link to={post.link || "#"} className="inline-flex items-center text-sm font-bold uppercase tracking-widest text-primary transition-colors hover:text-primary/80">
                Read Analysis
                <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function LatestAnalysis() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="241630" id="latest-analysis" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Latest Analysis
            </h2>
            <p className="mt-4 text-xl text-muted-foreground font-sans leading-relaxed">
              Authoritative perspectives on family law, real estate regulations, and 
              strategic legal advisory from Sterling Law Associates.
            </p>
          </div>
          <div className="shrink-0">
            <Link to="/LegalInsightsPage">
              <Button variant="outline" className="group rounded-md border-secondary font-mono uppercase tracking-widest text-xs px-8 py-6">
                View All Insights
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}